import { ReceiptIcon, SearchIcon } from 'lucide-react';

import { useEffect, useMemo, useState } from 'react';

import { formatDate } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import { formatAmountForCurrency } from '@splitfiesta/shared';
import type { ExpenseResponseDto } from '@splitfiesta/shared';
import { Card, EmptyState } from '@splitfiesta/ui';

import { ExpensesTable } from '@/components/expenses/ExpensesTable';

import { useRouter } from '@/hooks/useRouteChangeRouter';

import { resolveUserName } from '@/lib/displayName';
import {
  borderClasses,
  focusRingClasses,
  textColorClasses,
} from '@/lib/styles/common';

import { useExpensesFilter, useSetExpensesFilter } from '@/stores/uiStore';

interface ExpensesSectionProps {
  groupId: string;
  expenses: ExpenseResponseDto[];
  loading: boolean;
  hasMore?: boolean;
  onLoadMore: () => void;
  onDelete: (expenseId: string) => void;
  lastElementRef?: (node: HTMLElement | null) => void;
  isFetchingNextPage?: boolean;
  currentUserId?: string;
  groupMembers?: Array<{
    userId: string;
    role: 'owner' | 'admin' | 'member';
  }>;
  onSearchChange: (value: string) => void;
  /** Heading above the list. Defaults to the group page's "recent expenses". */
  title?: string;
  /** Drill-down reports own their filters; the search box only makes sense on the group page. */
  hideSearch?: boolean;
  /** Same for the All / Mine / Paid-by-me chips: a drill-down list must match its own chart. */
  hideFilters?: boolean;
  /**
   * Neutral empty-state title. A drill-down's empty list means "nothing
   * matched this filter", not "this group has no expenses", so the "add
   * expense" call to action and its description are dropped with it.
   */
  emptyTitle?: string;
}

export function ExpensesSection({
  groupId,
  expenses,
  loading,
  hasMore,
  onLoadMore,
  onDelete,
  lastElementRef,
  isFetchingNextPage,
  currentUserId,
  groupMembers,
  onSearchChange,
  title,
  hideSearch = false,
  hideFilters = false,
  emptyTitle,
}: ExpensesSectionProps) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const [searchQuery, setSearchQuery] = useState('');

  // Load dashboard namespace for filter labels
  useNamespace('dashboard');

  // Expenses filter settings
  const storedFilterMode = useExpensesFilter();
  const setExpensesFilter = useSetExpensesFilter();
  // The stored mode is the group page's; a caller that hides the chips must
  // not inherit it, or its list would silently disagree with its own chart.
  const filterMode = hideFilters ? 'all' : storedFilterMode;

  // Auto-load all pages when a filter is active so client-side filtering
  // sees every expense, not just the first page. A page that came back with
  // no rows at all needs the same push whatever the mode: the table's
  // sentinel row only exists when there are rows, so nothing else would ask.
  useEffect(() => {
    if (
      (filterMode !== 'all' || expenses.length === 0) &&
      hasMore &&
      !isFetchingNextPage &&
      !loading
    ) {
      onLoadMore();
    }
  }, [
    filterMode,
    expenses.length,
    hasMore,
    isFetchingNextPage,
    loading,
    onLoadMore,
  ]);

  useEffect(() => {
    const timer = setTimeout(() => {
      onSearchChange(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, onSearchChange]);

  // Filter expenses based on user participation
  const filteredExpenses = useMemo(() => {
    if (filterMode === 'all' || !currentUserId) return expenses;

    if (filterMode === 'paidByMe') {
      return expenses.filter(expense =>
        expense.payers?.some(p => p.userId === currentUserId)
      );
    }

    // 'involved': payers OR splits
    return expenses.filter(expense => {
      if (expense.payers?.some(p => p.userId === currentUserId)) return true;
      if (expense.splits?.some(s => s.userId === currentUserId)) return true;
      return false;
    });
  }, [expenses, filterMode, currentUserId]);

  const handleDelete = async (expenseId: string) => {
    const expense = expenses.find(e => e.id === expenseId);
    if (!expense) return;

    let payerName = 'Unknown';
    if (expense.payers && expense.payers.length > 0) {
      if (expense.payers.length === 1) {
        const payer = expense.payers[0];
        payerName = resolveUserName(payer.userName, t);
      } else {
        payerName = t('expenses:labels.multiplePayers', {
          count: expense.payers.length,
        });
      }
    }

    const confirmMessage =
      `${t('expenses:confirmDelete')}\n\n` +
      `${t('expenses:fields.title')}: ${expense.title}\n` +
      `${t('expenses:fields.amount')}: ${formatAmountForCurrency(expense.amount, expense.currency, i18n.language)}\n` +
      `${t('expenses:labels.paidBy', { name: payerName })}\n` +
      `${t('expenses:fields.date')}: ${formatDate(expense.date, i18n.language)}\n` +
      `${expense.category ? `${t('expenses:fields.category')}: ${t(`expenses:categories.${expense.category}`, expense.category)}` : ''}`;

    if (!confirm(confirmMessage)) {
      return;
    }

    try {
      await onDelete(expenseId);
    } catch {
      // Reported by useDeleteExpenseMutation's onError toast.
    }
  };

  return (
    <div data-testid="group-expenses-section">
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {title ?? t('groups:sections.recentExpenses')}
        </h3>
      </div>

      {/* Search first, then the filter pills — the same order as mobile. */}
      {!hideSearch && (
        <div className="mb-4">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('common:placeholders.searchExpenses')}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className={`w-full pl-10 pr-4 py-2 border ${borderClasses.light} rounded-lg bg-white dark:bg-gray-700 ${textColorClasses.primary} placeholder-gray-500 dark:placeholder-gray-400 ${focusRingClasses}`}
              data-testid="expense-search-input"
            />
          </div>
        </div>
      )}

      {!hideFilters && (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          <button
            type="button"
            onClick={() => setExpensesFilter('all')}
            className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
              filterMode === 'all'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            data-testid="expenses-filter-all"
          >
            {t('dashboard:activityFeed.filterAll')}
          </button>
          <button
            type="button"
            onClick={() => setExpensesFilter('involved')}
            className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
              filterMode === 'involved'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            data-testid="expenses-filter-mine"
          >
            {t('dashboard:activityFeed.filterMine')}
          </button>
          <button
            type="button"
            onClick={() => setExpensesFilter('paidByMe')}
            className={`px-3 py-1.5 text-sm font-medium rounded-full whitespace-nowrap transition-colors ${
              filterMode === 'paidByMe'
                ? 'bg-orange-500 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
            data-testid="expenses-filter-paid-by-me"
          >
            {t('dashboard:activityFeed.filterPaidByMe')}
          </button>
        </div>
      )}

      {filteredExpenses.length === 0 && !loading && !hasMore ? (
        <Card>
          <EmptyState
            icon={<ReceiptIcon className="w-12 h-12" />}
            title={emptyTitle ?? t('groups:empty.noExpenses')}
            description={
              emptyTitle ? undefined : t('groups:empty.noExpensesDescription')
            }
            action={
              emptyTitle
                ? undefined
                : {
                    label: t('expenses:actions.addExpense'),
                    icon: <ReceiptIcon className="w-5 h-5" />,
                    onClick: () =>
                      router.push(`/groups/${groupId}/expenses/create`),
                  }
            }
          />
        </Card>
      ) : (
        <ExpensesTable
          expenses={filteredExpenses.map(expense => ({
            ...expense,
            groupId,
          }))}
          onDelete={handleDelete}
          loading={loading && filteredExpenses.length === 0}
          lastElementRef={lastElementRef}
          showLoadingMore={isFetchingNextPage}
          currentUserId={currentUserId}
          groupMembers={groupMembers}
        />
      )}
    </div>
  );
}
